import React from 'react';
import { formatRupiah } from '../../utils/formatPrice';

type Doctor = {
  name: string;
  specialization: string;
  years: number;
  rating: number;
  hospital: string;
  price_chat: number;
  price_video: number;
  originalPrice?: number;
  photoUrl: string;
  is_online?: boolean; // Optional property to indicate if the doctor is online
  availability?: string;
  isMyOngoing?: boolean;
};

const AVAILABILITY_BADGE: Record<string, { label: string; dot: string; text: string }> = {
  ONLINE: { label: 'Online', dot: 'bg-green-500', text: 'text-green-600' },
  BUSY: { label: 'Slot penuh', dot: 'bg-orange-400', text: 'text-orange-500' },
  OFFLINE: { label: 'Offline', dot: 'bg-red-500', text: 'text-red-500' },
  DISABLED: { label: 'Tidak tersedia', dot: 'bg-gray-400', text: 'text-gray-500' },
};

interface DoctorCardProps {
  doctor: Doctor;
  onConsult?: () => void;
  isDokterUmum?: boolean;
}


const DoctorCard: React.FC<DoctorCardProps> = ({ doctor, onConsult, isDokterUmum = true }) => {
  const availability = doctor.availability ?? (doctor.is_online ? 'ONLINE' : 'OFFLINE');
  const badge = AVAILABILITY_BADGE[availability] ?? AVAILABILITY_BADGE.OFFLINE;
  const isDisabled = availability !== 'ONLINE' || doctor.isMyOngoing || !isDokterUmum;
  
  const handleConsult = () => {
    if (isDisabled) return;
    onConsult?.();
  };
  
  return (
    <div className="flex items-center bg-white p-4 rounded-xl shadow mb-4">
      {/* Foto */}
      <div className="relative mr-4 shrink-0">
        <img
          src={doctor.photoUrl}
          alt={doctor.name}
          onError={(e) => {
            e.currentTarget.src =
              'https://m.linksehat.com/assets/img/users/dummy.jpg';
          }}
          className="w-16 h-16 rounded-lg object-cover"
        />
        <span
          className={`w-3 h-3 rounded-full absolute bottom-0 right-0 border-2 border-white ${badge.dot}`}
        />
      </div>

      {/* Info Dokter */}
      <div className="flex-1 min-w-0">
        {/* Nama */}
        <h4 className="font-semibold truncate">
          {doctor.name}
        </h4>

        {/* Spesialis */}
        <p className="text-sm text-gray-500 truncate">
          {doctor.specialization}
        </p>

        {/* Experience */}
        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
          <div className="flex items-center gap-1 whitespace-nowrap">
            <img src="/app-icons/briefcase.svg" alt="Years" className="w-4 h-4" />
            <span>{doctor.years} yrs</span>
          </div>

          <div className="flex items-center gap-1 whitespace-nowrap">
            <img src="/app-icons/thumb-up.svg" alt="Rating" className="w-4 h-4" />
            <span>{doctor.rating}%</span>
          </div>
        </div>

        <p className="text-sm mt-1">
          {formatRupiah(doctor.price_chat)}
        </p>

        <p className={`text-xs font-medium mt-0.5 ${badge.text}`}>
          {badge.label}
        </p>
      </div>

      {/* Tombol */}
      <div className="ml-4 shrink-0">
        <button
          className={`px-4 py-1 rounded-full text-sm whitespace-nowrap transition-opacity
            ${availability === 'BUSY'
              ? 'bg-amber-50 text-amber-700 border border-amber-200 cursor-not-allowed'
              : isDisabled
                ? 'text-white bg-gray-400 opacity-50'
                : 'text-white bg-morning-300 cursor-pointer'
            }`}
          onClick={handleConsult}
          disabled={isDisabled}
          aria-label={availability === 'BUSY' ? `Slot konsultasi ${doctor.name} sedang penuh` : undefined}
        >
          {availability === 'BUSY' ? 'Slot Penuh' : 'Konsultasi'}
        </button>
      </div>
    </div>
  );
};

export default DoctorCard;
